// src/utils/api.js — central fetch wrapper
import { getToken, removeToken } from './auth';

export const API_BASE = (typeof process !== 'undefined' && process.env.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL
  : window.location.origin.replace(/:\d+$/, ':5000');

export async function apiFetch(path, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (!(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    let res = await fetch(API_BASE + path, { ...opts, headers });

    // Auto-refresh: If 401 and not a login/refresh request, try to refresh
    if (res.status === 401 && !path.includes('/api/login') && !path.includes('/api/refresh')) {
      console.warn('[API] Access token expired, attempting refresh...');
      
      try {
        const refreshRes = await fetch(API_BASE + '/api/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // The refresh token is in an HttpOnly cookie, so it's sent automatically
          credentials: 'include' 
        });

        if (refreshRes.ok) {
          const { token: newToken } = await refreshRes.json();
          const { setToken } = await import('./auth'); // Dynamic import to avoid circular dependency
          setToken(newToken);
          
          // Retry the original request with the new token
          headers['Authorization'] = `Bearer ${newToken}`;
          res = await fetch(API_BASE + path, { ...opts, headers });
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshErr) {
        console.error('[API] Refresh failed, logging out:', refreshErr);
        const { removeToken } = await import('./auth');
        removeToken();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Session expired');
      }
    }

    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Error ${res.status}: ${res.statusText}`);
      return data;
    }

    if (!res.ok) throw new Error(`Server error ${res.status}: ${res.statusText}`);
    return res.text();
  } catch (err) {
    console.error(`[API FETCH ERROR] ${path}:`, err);
    if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
      throw new Error('Network error: Unable to connect to server.');
    }
    throw err;
  }
}
